import React, { useEffect, useState} from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TaskIcon from '@mui/icons-material/Task';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArticleIcon from '@mui/icons-material/Article';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import './sidebar.css';
import logo2 from '../../Assets/Img/logo.png';
import { Link } from 'react-router-dom';
import { useAuth } from '../../Auth/AuthContext';
import { signOut } from 'aws-amplify/auth';
import VERSION_INFO from '../../version';

const drawerWidth = 250;

const AppBar = styled(MuiAppBar)(() => ({
  backgroundColor: '#ffffff',
  height: '4.5rem',
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

export default function Sidebar({ hideHamburger = false }) {
  const [open, setOpen] = React.useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, userDetails } = useAuth();

  // Open/close Drawer handlers
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  // Profile popover handlers - using click instead of hover
  const handlePopoverToggle = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  // Close Drawer when route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await signOut();
  };

   useEffect(() => {
    const fetchUserGroups = async () => {
      try {
        const authSession = await fetchAuthSession();
        if (authSession?.tokens?.idToken?.payload) {
          const groups = authSession.tokens.idToken.payload['cognito:groups'] || [];
          setUserGroups(groups);
        }

      } catch (error) {
        console.error('Error fetching user groups:', error);
      }
    };
    
    fetchUserGroups();
  }, []);

  // Navigation items - base items for all users
  const baseNavItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/dash' },
    { text: 'Employee Scheduling', icon: <CalendarMonthIcon />, path: '/employee-scheduling' },
    { text: "Routes Summary", icon: <TaskIcon />, path: '/routes-option-1' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
    { text: 'Email Settings', icon: <ManageAccountsIcon />, path: '/my-account' },
    { text: "Manage Subscriptions", icon: <PaymentIcon/>, path: '/billing'},
    { text: 'Recovery Templates', icon: <ArticleIcon />, path: '/templates' },
    { text: 'Help & FAQ', icon: <HelpOutlineIcon />, path: '/help-faq' },
  ];

  // Admin-only navigation item
  const adminNavItem = {
    text: 'Admin',
    icon: <PersonIcon />,
    path: '/admin'
  };

  const role = userDetails?.role?userDetails?.role:null;

  // Combine nav items based on user groups
  const navItems = [
    ...baseNavItems,
    ...((role!=null && role === 'Admin' )? [adminNavItem] : [])
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar>
          {!open && !hideHamburger && (
            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon sx={{ fontSize: '2rem', color: '#002244' }} />
            </IconButton>
          )}

          <Link to={ hideHamburger ? "/admin" : "/dash/"}>
          <img
            src={logo2}
            alt="logo2"
            className="logo2"
            style={{ height: '4rem', marginRight: '1rem', marginLeft: '0.5rem' }}
          />
          </Link>

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: '#002244',
              fontWeight: 500,
              fontSize: '1.5rem',
              fontFamily: 'Poppins, sans-serif',
              flexGrow: 1,
            }}
          >
            POOL ROUTE MONITOR
          </Typography>

          {/* Profile Icon */}
          <IconButton
            aria-owns={openPopover ? 'profile-popover' : undefined}
            aria-haspopup="true"
            onClick={handlePopoverToggle}
            sx={{ marginLeft: 'auto' }}
          >
            <Avatar sx={{ bgcolor: '#002244', width: 40, height: 40 }}>
              <AccountCircleIcon sx={{ fontSize: '2rem' }} />
            </Avatar>
          </IconButton>

          <Popover
            id="profile-popover"
            open={openPopover}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={handlePopoverClose}
          >
            <Box
              sx={{
                p: 2,
                minWidth: 250,
                backgroundColor: '#ffffff',
                border: '2px solid #002244',
                borderRadius: '8px',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#002244',
                  fontWeight: 600,
                  marginBottom: '10px',
                  fontSize: '1.1rem',
                }}
              >
                User Profile
              </Typography>
              <Divider sx={{ marginBottom: '10px', backgroundColor: '#002244' }} />

              {userDetails ? (
                <>
                  <Box sx={{ marginBottom: '8px' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#002244', fontSize: '0.9rem' }}
                    >
                      Company:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem' }}>
                      {userDetails.companyName}
                    </Typography>
                  </Box>

                  <Box sx={{ marginBottom: '8px' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#002244', fontSize: '0.9rem' }}
                    >
                      Name:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem' }}>
                      {userDetails.name}
                    </Typography>
                  </Box>

                  <Box sx={{ marginBottom: '8px' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#002244', fontSize: '0.9rem' }}
                    >
                      Email:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem' }}>
                      {userDetails.email}
                    </Typography>
                  </Box>

                  <Box sx={{ marginBottom: '8px' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#002244', fontSize: '0.9rem' }}
                    >
                      Phone:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem' }}>
                      {userDetails.phone}
                    </Typography>
                  </Box>

                  <Box sx={{ marginBottom: '8px' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#002244', fontSize: '0.9rem' }}
                    >
                      Designation:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', fontSize: '0.9rem' }}>
                      {userDetails.designation}
                    </Typography>
                  </Box>

                  <Divider sx={{ marginY: '10px', backgroundColor: '#002244' }} />

                  <Button
                    variant="contained"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    fullWidth
                    sx={{
                      backgroundColor: '#002244',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#001a33',
                      },
                    }}
                  >
                    Logout
                  </Button>

                  <Divider sx={{ marginY: '10px', backgroundColor: '#002244' }} />

                  {/* Version Information */}
                  <Box sx={{ textAlign: 'center', marginTop: '10px' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                      }}
                    >
                      Version {VERSION_INFO.version}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#999',
                        fontSize: '0.65rem',
                      }}
                    >
                      {VERSION_INFO.environment}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Loading user information...
                </Typography>
              )}
            </Box>
          </Popover>

        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'white',
            color: '#002244',
            borderRight: '2px solid #002244',
          },
        }}
        variant="temporary"
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <CloseIcon sx={{ fontSize: '2.5rem', color: '#002244' }} />
          </IconButton>
        </DrawerHeader>
        <Typography
          variant="h6"
          sx={{
            color: '#002244',
            textAlign: 'center',
            padding: '0.5rem 0 0.3rem 0',
            fontSize: '1.2rem',
            fontWeight: 500,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Menu
        </Typography>
        <Divider sx={{ border: '1.5px solid #002244', width: '60%', margin: '0 auto 0.3rem' }} />
        <List sx={{ margin: '0', flexGrow: 1, overflowY: 'auto' }}>
          {navItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setOpen(false);
                }}
                sx={{
                  color: '#002244',
                  borderRadius: '8px',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                  margin: '4px 12px',
                  padding: '6px 12px',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#002244', fontSize: '1.2rem', minWidth: '35px' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <List sx={{ mt: 'auto', flexShrink: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                color: '#002244',
                borderRadius: '8px',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                margin: '4px 12px',
                padding: '6px 12px',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.03)',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#002244', fontSize: '1.2rem', minWidth: '35px' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Log Out"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>

          {/* Version Information */}
          <ListItem sx={{ paddingY: '2px' }}>
            <Box sx={{ textAlign: 'center', width: '100%', paddingY: '2px' }}>
              <Divider sx={{ marginBottom: '4px', backgroundColor: '#e0e0e0' }} />
              <Typography
                variant="caption"
                sx={{
                  color: '#666',
                  fontSize: '0.65rem',
                  fontStyle: 'italic',
                  display: 'block',
                }}
              >
                Version {VERSION_INFO.version}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: '#999',
                  fontSize: '0.55rem',
                }}
              >
                {VERSION_INFO.environment}
              </Typography>
            </Box>
          </ListItem>
        </List>
      </Drawer>
    </Box>
  );
}
